import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const invite = searchParams.get("invite") === "1";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(invite ? "You're all set — sign in with your new password" : "Password has been reset");
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-[#121212] border border-white/10 rounded-xl p-8" data-testid="reset-password-form">
        <div className="flex justify-center mb-6"><Logo /></div>
        <h1 className="font-heading font-bold text-white text-xl text-center mb-1">
          {invite ? "Welcome to NewsLayer" : "Reset your password"}
        </h1>
        <p className="text-gray-400 text-sm text-center mb-8">
          {invite ? "Choose a password to activate your seat and sign in." : "Choose a new password for your account."}
        </p>
        {!token ? (
          <p className="text-red-400 text-sm text-center" data-testid="reset-password-no-token">
            This link is missing its token — please use the link from your email.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-gray-300">New password (min 8 characters)</Label>
              <Input type="password" required minLength={8} value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input"
                className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Confirm password</Label>
              <Input type="password" required minLength={8} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm-input"
                className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="reset-password-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="reset-password-submit"
              className="w-full bg-[#7C3AED] text-white font-bold hover:bg-[#6b2fd6]">
              {busy ? "Saving…" : invite ? "Activate my seat" : "Reset password"}
            </Button>
          </form>
        )}
        <p className="text-center mt-6">
          <Link to="/login" className="font-mono text-xs text-gray-500 hover:text-[#00E5FF] transition-colors">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
